import { apiFirst } from '@/core/api/resources'
import type { Me as AuthSession } from '@/core/api/auth'

export type BootstrapResult = { userId: string; organisationId: string }

export class NeedsOrganisation extends Error {
  constructor() {
    super("bootstrap: no organisation yet — the user creates one")
    this.name = 'NeedsOrganisation'
  }
}

let inFlight: { userId: string; promise: Promise<BootstrapResult> } | null = null

export function bootstrapMe(session: AuthSession): Promise<BootstrapResult> {
  const uid = session.user_id
  if (!inFlight || inFlight.userId !== uid) {
    const promise = doBootstrap(session).catch((e: unknown) => {
      if (inFlight?.promise === promise) inFlight = null
      throw e
    })
    inFlight = { userId: uid, promise }
  }
  return inFlight.promise
}

const MEMBERSHIP_RETRY_DELAY_MS = 800

async function readMembership(userId: string): Promise<{ organisation_id: string } | null> {

  const row = await apiFirst('/mitgliedschaft', { member_id: userId, limit: 1 })
  return row ? { organisation_id: row.organisation_id } : null
}

function wasInvitedByAnotherUser(createdBy: string | null, ownId: string): boolean {
  return createdBy !== null && createdBy !== ownId
}

async function doBootstrap(session: AuthSession): Promise<BootstrapResult> {

  const mine = await apiFirst('/members', { auth_user_id: session.user_id, limit: 1 })
  if (!mine) {
    throw new Error(
      "bootstrap: no members row for this identity — the gate authenticated an account with no domain person row",
    )
  }
  const userId = mine.id
  const wasInvited = wasInvitedByAnotherUser(mine.created_by, session.user_id)

  let membership = await readMembership(userId)
  if (!membership) {
    await new Promise((resolve) => setTimeout(resolve, MEMBERSHIP_RETRY_DELAY_MS))
    membership = await readMembership(userId)
  }
  if (membership) return { userId, organisationId: membership.organisation_id }

  if (wasInvited) {

    throw new Error(
      "bootstrap: this user was invited by another account but has no visible membership yet — refusing to create a new organisation for them",
    )
  }

  throw new NeedsOrganisation()
}
